/**
 * AISectionsSearchBar - Search input for AI Sections
 *
 * Pill-shaped input field matching the AISectionsButton styling, allowing users
 * to type custom questions instead of clicking predefined buttons.
 */

import { type ChangeEvent, type KeyboardEvent, useRef, useState } from 'react';
import { useRevealAnimation } from '../../hooks/ui/useRevealAnimation';
import { getContrastingTextColor } from '../../utils/ui/colorUtils';
import ArrowUpIcon from '../ArrowUpIcon';
import SparkleIcon from '../SparkleIcon';

// Hoisted placeholder styles - always needed for the search input
const PLACEHOLDER_STYLES = (
    <style>{`
        .ai-sections-search-input::placeholder {
            color: inherit;
            opacity: 0.5;
        }
    `}</style>
);

export interface AISectionsSearchBarProps {
    /** Placeholder text for the input */
    placeholder: string;
    /** Primary color for styling (sparkle icon) */
    primaryColor?: string;
    /** Background color for the search bar */
    backgroundColor?: string;
    /** Text color (auto-calculated if not provided) */
    textColor?: string;
    /** Font size for input text */
    fontSize?: string;
    /** Font weight for input text */
    fontWeight?: string;
    /** Size of sparkle icon in pixels */
    iconSize?: number;
    /** Size of arrow icon in pixels */
    arrowSize?: number;
    /** Whether to show the search bar (controls entrance animation) */
    show?: boolean;
    /** Delay before showing (for staggered animation) */
    animationDelay?: number;
    /** Whether to show shimmer effect on first reveal */
    showShimmer?: boolean;
    /** Use inline style for background */
    useInlineBackground?: boolean;
    /** Use inline style for text color */
    useInlineTextColor?: boolean;
    /** Use inline style for font size */
    useInlineFontSize?: boolean;
    /** Use inline style for font weight */
    useInlineFontWeight?: boolean;
    /** Whether this is the only element (no question buttons) - makes it wider */
    isStandalone?: boolean;
    /** Callback when user submits a question */
    onSubmit: (question: string) => void;
}

export function AISectionsSearchBar({
    placeholder,
    primaryColor,
    backgroundColor = '#FFFFFF',
    textColor,
    fontSize = '20px',
    fontWeight = '400',
    iconSize = 20,
    arrowSize = 20,
    show = true,
    animationDelay = 0,
    showShimmer = true,
    useInlineBackground = true,
    useInlineTextColor = true,
    useInlineFontSize = true,
    useInlineFontWeight = true,
    isStandalone = false,
    onSubmit,
}: AISectionsSearchBarProps) {
    const [inputValue, setInputValue] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Use shared reveal animation hook
    const { isRevealed, showShimmerEffect, cssSparkleColor } = useRevealAnimation({
        show,
        animationDelay,
        showShimmer,
        containerRef,
    });

    // Compute effective text color (only when using inline styles)
    const effectiveTextColor = useInlineTextColor
        ? textColor || getContrastingTextColor(backgroundColor)
        : undefined;

    // Sparkle color: CSS variable override > primaryColor (from site config)
    const effectiveSparkleColor = cssSparkleColor || primaryColor;

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && inputValue.trim()) {
            e.preventDefault();
            onSubmit(inputValue.trim());
            setInputValue('');
        }
    };

    const handleSubmitClick = () => {
        if (inputValue.trim()) {
            onSubmit(inputValue.trim());
            setInputValue('');
        }
    };

    const hasInput = inputValue.trim().length > 0;

    return (
        // biome-ignore lint/a11y/useKeyWithClickEvents lint/a11y/noStaticElementInteractions: click delegates focus to the inner input; keyboard users can tab directly
        <div
            ref={containerRef}
            className="ai-sections-search-bar"
            onClick={() => inputRef.current?.focus()}
            style={{
                cursor: 'text',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 16px',
                borderRadius: '9999px',
                border: 'none',
                outline: 'none',
                transition: 'all 0.3s ease-out',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                position: 'relative',
                overflow: 'hidden',
                // Full width, with max constraint only when not standalone
                // No minWidth - allows natural responsiveness on mobile
                width: '100%',
                ...(isStandalone ? {} : { maxWidth: '500px' }),
                ...(useInlineBackground ? { background: backgroundColor } : {}),
                opacity: isRevealed ? 1 : 0,
                transform: isRevealed ? 'translateY(0)' : 'translateY(12px)',
                transitionDelay: `${animationDelay}ms`,
                pointerEvents: isRevealed ? 'auto' : 'none',
            }}
        >
            {/* Sparkle icon */}
            <SparkleIcon
                size={iconSize}
                color={effectiveSparkleColor}
                style={{ flexShrink: 0 }}
                className="ai-sections-icon"
            />

            {/* Input field */}
            <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                aria-label={placeholder}
                className="ai-sections-search-input"
                style={{
                    flex: 1,
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    ...(useInlineTextColor ? { color: effectiveTextColor } : {}),
                    ...(useInlineFontWeight ? { fontWeight } : {}),
                    ...(useInlineFontSize ? { fontSize } : {}),
                    lineHeight: 1,
                    minWidth: 0,
                }}
            />

            {/* Submit button - sized to match icons, not add extra height */}
            <button
                type="button"
                onClick={handleSubmitClick}
                disabled={!hasInput}
                aria-label="Submit question"
                className="ai-sections-search-submit"
                style={{
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: arrowSize,
                    height: arrowSize,
                    borderRadius: '50%',
                    border: 'none',
                    padding: 0,
                    cursor: hasInput ? 'pointer' : 'default',
                    background: hasInput ? effectiveSparkleColor : 'transparent',
                    transition: 'all 0.2s ease-out',
                    opacity: hasInput ? 1 : 0.4,
                }}
            >
                <ArrowUpIcon
                    width={arrowSize - 4}
                    height={arrowSize - 4}
                    color={
                        hasInput ? '#FFFFFF' : useInlineTextColor ? effectiveTextColor : undefined
                    }
                    style={{ flexShrink: 0 }}
                    className="ai-sections-search-arrow"
                />
            </button>

            {/* Shimmer effect */}
            {showShimmerEffect && (
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        pointerEvents: 'none',
                        background:
                            'linear-gradient(135deg, transparent 30%, rgba(255,255,255,0.4) 50%, transparent 70%)',
                        transform: 'translateX(-100%) translateY(-100%)',
                        animation: 'rw-diagonal-shimmer 0.8s ease-out forwards',
                        width: '200%',
                        height: '200%',
                    }}
                />
            )}

            {/* Hoisted placeholder styles - always needed */}
            {PLACEHOLDER_STYLES}
        </div>
    );
}
